import moment from 'moment'

/**
 * Task due dates are calendar days, not instants.
 *
 * The API stores `dueDate` as midnight UTC of the intended calendar day
 * (an input of `2026-08-11` round-trips as `2026-08-11T00:00:00.000Z`).
 * Reading that back with plain `moment(dueDate)` lands on Aug 10, 8pm in any
 * timezone west of UTC, so a task due today classifies as overdue, and
 * comparing it against `new Date()` calls it overdue from midnight onward even
 * in UTC. Both readings are instant comparisons applied to a date-only value.
 *
 * Everything in here works in whole local calendar days instead: pull the UTC
 * `YYYY-MM-DD` portion, re-parse it as a local start-of-day moment, and compare
 * days against days.
 */

export type TaskDueState = `completed` | `overdue` | `dueToday` | `upcoming` | `none`

type DueDate = string | Date

type DueTask = {
  dueDate?: DueDate | null
  status?: string | null
}

/** The task's due date as a local start-of-day moment on its intended calendar day. */
export function dueDateAsDay(dueDate: DueDate): moment.Moment {
  return moment(moment.utc(dueDate).format(`YYYY-MM-DD`))
}

/** Start of the current local day. Pass `now` to pin the reference point (tests, batch math). */
export function startOfToday(now?: moment.MomentInput): moment.Moment {
  return moment(now).startOf(`day`)
}

/** Where a task sits relative to today: completed, overdue, due today, upcoming, or undated. */
export function taskDueState(task: DueTask, now?: moment.MomentInput): TaskDueState {
  if (task.status === `closed`) return `completed`
  if (!task.dueDate) return `none`

  const due = dueDateAsDay(task.dueDate)
  const today = startOfToday(now)
  if (due.isBefore(today)) return `overdue`
  if (due.isSame(today)) return `dueToday`
  return `upcoming`
}

export function isTaskOverdue(task: DueTask, now?: moment.MomentInput): boolean {
  return taskDueState(task, now) === `overdue`
}

export function isTaskDueToday(task: DueTask, now?: moment.MomentInput): boolean {
  return taskDueState(task, now) === `dueToday`
}

/** Whole calendar days a task is past due. 0 when it is due today or later. */
export function overdueDays(dueDate: DueDate, now?: moment.MomentInput): number {
  return Math.max(0, startOfToday(now).diff(dueDateAsDay(dueDate), `days`))
}
